import { useRef, useState, type DragEvent } from 'react';
import { Upload, FileText, X } from 'lucide-react';
import styles from './UploadDropzone.module.css';

interface Props {
  onText: (text: string) => void;
  onError: (message: string | null) => void;
  className?: string;
}

const ACCEPT = '.txt,.md,.html,.htm,text/plain,text/markdown,text/html';
const MAX_BYTES = 600_000; // matches backend cap

function stripHtmlTags(html: string): string {
  // Lightweight: drop <script>/<style> entirely, then strip remaining tags.
  const cleaned = html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '');
  const txt = cleaned.replace(/<[^>]+>/g, ' ');
  return txt.replace(/\s+/g, ' ').trim();
}

export function UploadDropzone({ onText, onError, className }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [filename, setFilename] = useState<string | null>(null);

  const accept = (file: File) => {
    onError(null);
    if (file.size > MAX_BYTES) {
      onError(`File is too large (${(file.size / 1024).toFixed(0)}KB). Trim to under ${MAX_BYTES / 1024}KB.`);
      return;
    }
    const lower = file.name.toLowerCase();
    const isText = file.type.startsWith('text/') ||
      lower.endsWith('.txt') || lower.endsWith('.md') ||
      lower.endsWith('.html') || lower.endsWith('.htm');
    if (!isText) {
      onError('Only .txt, .md, and .html files are supported. Convert PDFs / DOCXs to text first.');
      return;
    }
    file.text().then((raw) => {
      const content = (lower.endsWith('.html') || lower.endsWith('.htm') || file.type === 'text/html')
        ? stripHtmlTags(raw)
        : raw;
      if (content.trim().length < 200) {
        onError('File is too short to analyse — needs at least a few paragraphs of text.');
        return;
      }
      setFilename(file.name);
      onText(content);
    }).catch(() => {
      onError('Could not read the file. Try copy-pasting the text instead.');
    });
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) accept(file);
  };

  return (
    <div
      className={[
        styles.dropzone,
        dragOver ? styles.dragOver : '',
        className,
      ].filter(Boolean).join(' ')}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className={styles.hiddenInput}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) accept(file);
        }}
        aria-label="Choose a text file to upload"
      />
      {filename ? (
        <div className={styles.acceptedRow}>
          <FileText size={20} strokeWidth={1.5} aria-hidden="true" />
          <span className={styles.acceptedName}>{filename}</span>
          <button
            type="button"
            className={styles.clearBtn}
            onClick={(e) => {
              e.stopPropagation();
              setFilename(null);
              onText('');
              if (inputRef.current) inputRef.current.value = '';
            }}
            aria-label="Clear uploaded file"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        </div>
      ) : (
        <>
          <Upload size={32} strokeWidth={1.5} aria-hidden="true" className={styles.icon} />
          <p className={styles.headline}>Drop a .txt, .md or .html file here</p>
          <p className={styles.hint}>
            <button type="button" className={styles.browseBtn} onClick={() => inputRef.current?.click()}>
              Or click to browse
            </button>
          </p>
          <p className={styles.note}>
            PDFs and DOCXs aren't supported in-browser yet — convert to text first.
          </p>
        </>
      )}
    </div>
  );
}
